import type { FC, FormEvent, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { QuoteServiceId } from '@/types'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import {
    ArrowLeft,
    Cable,
    Check,
    CircleCheckBig,
    DoorClosed,
    Gauge,
    LoaderCircle,
    MapPin,
    Navigation,
    PanelTop,
    ScanSearch,
    Siren,
    Smartphone,
    Trash2,
    TriangleAlert,
    Warehouse,
    Wrench
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/generals/button'
import { TransitionLink } from '@/components/generals/transition-link'
import { useQuoteList } from '@/components/generals/quote-list-provider'
import { findAreaByZip } from '@/constants/areas'
import { formServices, getFormService } from '@/constants/quote-form'
import { siteConfig } from '@/constants/site'

import {
    OptionGrid,
    PhotoInput,
    TextField,
    TimingPicker,
    ASAP_SLOT
} from '@/components/widgets/quote-form-parts'

type StepId = 'service' | 'zip' | 'details' | 'urgency' | 'timing' | 'contact'

type Answers = Record<string, string | string[]>

type Visit = { day: string, slot: string, label: string }

const EMPTY_VISIT: Visit = { day: '', slot: '', label: '' }

const serviceIcons: Record<QuoteServiceId, LucideIcon> = {
    spring: Wrench,
    opener: Smartphone,
    offtrack: Cable,
    panel: PanelTop,
    'new-door': DoorClosed,
    commercial: Warehouse,
    emergency: Siren,
    'tune-up': Gauge,
    'diagnosis-visit': ScanSearch
}

const hasAnswer = (value: string | string[] | undefined): boolean =>
    Array.isArray(value) ? value.length > 0 : Boolean(value && value.trim())

interface QuoteFormProps {
    initialService?: QuoteServiceId | null
    initialAnswers?: Answers
    entry?: string
    className?: string
}

export const QuoteForm: FC<QuoteFormProps> = ({
    initialService = null,
    initialAnswers = {},
    entry = 'form',
    className
}): ReactNode => {
    const router = useRouter()
    const { items, remove, clear } = useQuoteList()
    const startConfig = initialService ? getFormService(initialService) : undefined

    const [service, setService] = useState<QuoteServiceId | null>(initialService)
    const [stepIndex, setStepIndex] = useState(initialService ? 1 : 0)
    const [direction, setDirection] = useState<1 | -1>(1)
    const [zip, setZip] = useState('')
    const [answers, setAnswers] = useState<Answers>(initialAnswers)
    const [urgency, setUrgency] = useState<string>(
        typeof initialAnswers.urgency === 'string' ? initialAnswers.urgency : startConfig?.urgency.defaultAnswer ?? ''
    )
    const [visit, setVisit] = useState<Visit>(EMPTY_VISIT)
    const [contact, setContact] = useState({ name: '', phone: '', email: '', company: '' })
    const [notes, setNotes] = useState('')
    const [photo, setPhoto] = useState<{ dataUrl: string, name: string } | null>(null)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle')
    const [result, setResult] = useState({ name: '', phone: '', when: '' })
    const [website, setWebsite] = useState('')

    const rootRef = useRef<HTMLDivElement>(null)
    const headingRef = useRef<HTMLHeadingElement>(null)
    const mountedRef = useRef(false)

    const config = service ? getFormService(service) : undefined

    useEffect(() => {
        if (service || items.length === 0) return

        const first = items[0]
        const firstConfig = getFormService(first.formService)

        setService(first.formService)
        setUrgency(current => current || firstConfig?.urgency.defaultAnswer || '')
        setAnswers(current => ({ ...(first.answers ?? {}), ...current }))
    }, [items, service])

    const steps = useMemo<StepId[]>(() => {
        const list: StepId[] = ['service', 'zip', 'details']

        if (!config?.urgency.preset) list.push('urgency')

        list.push('timing', 'contact')

        return list
    }, [config])

    const safeIndex = Math.min(stepIndex, steps.length - 1)
    const step = steps[safeIndex]
    const effectiveUrgency = config?.urgency.preset ?? urgency
    const urgentToday = service === 'emergency' || effectiveUrgency === "Today" || effectiveUrgency === "Yes, today"

    const visibleQuestions = (config?.questions ?? []).filter(question =>
        !question.showIf || question.showIf.answers.includes(String(answers[question.showIf.question] ?? ''))
    )

    const zipArea = /^\d{5}$/.test(zip) ? findAreaByZip(zip) : undefined

    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true

            return
        }

        headingRef.current?.focus({ preventScroll: true })

        const top = rootRef.current?.getBoundingClientRect().top ?? 0

        if (top < 80) rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, [safeIndex, status])

    const setAnswer = (id: string, value: string, multiple = false) => {
        setAnswers(current => {
            if (!multiple) return { ...current, [id]: value }

            const list = Array.isArray(current[id]) ? current[id] as string[] : []

            if (value === "None of these") return { ...current, [id]: list.includes(value) ? [] : [value] }

            const withoutNone = list.filter(entry => entry !== "None of these")

            return {
                ...current,
                [id]: withoutNone.includes(value) ? withoutNone.filter(entry => entry !== value) : [...withoutNone, value]
            }
        })

        setErrors(current => ({ ...current, [id]: '' }))
    }

    const chooseService = (id: QuoteServiceId) => {
        if (id !== service) {
            const next = getFormService(id)

            setService(id)
            setUrgency(next?.urgency.defaultAnswer ?? '')
            setVisit(EMPTY_VISIT)
        }

        setErrors({})
        setDirection(1)
        window.setTimeout(() => setStepIndex(1), 200)
    }

    const validate = (id: StepId): Record<string, string> => {
        const found: Record<string, string> = {}

        if (id === 'service' && !service) found.service = "Choose the service closest to your problem."
        if (id === 'zip' && !/^\d{5}$/.test(zip)) found.zip = "Please enter your 5-digit ZIP code."

        if (id === 'details') {
            visibleQuestions.forEach(question => {
                if (!question.optional && !hasAnswer(answers[question.id])) found[question.id] = "Choose an answer to keep going."
            })
        }

        if (id === 'urgency' && !urgency) found.urgency = "Let us know how soon you need us."
        if (id === 'timing' && !visit.slot) found.timing = "Choose a time window that works for you."

        if (id === 'contact') {
            if (contact.name.trim().length < 2) found.name = "Tell us your name so the technician knows who to ask for."
            if (contact.phone.replace(/\D/g, '').length < 10) found.phone = "Please enter a mobile number that can receive texts."
            if (contact.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) found.email = "Please check that email address."
        }

        return found
    }

    const submit = async () => {
        setStatus('submitting')

        const answerPayload: Answers = {}

        visibleQuestions.forEach(question => {
            if (hasAnswer(answers[question.id])) answerPayload[question.id] = answers[question.id]
        })

        if (typeof answers.diagnosis === 'string') answerPayload.diagnosis = answers.diagnosis

        const when = visit.slot === ASAP_SLOT ? "as soon as possible" : visit.label ? `for ${visit.label}` : ''

        try {
            const response = await fetch('/api/quote', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    service,
                    zip,
                    answers: answerPayload,
                    urgency: effectiveUrgency,
                    visitTime: visit.slot === ASAP_SLOT ? 'ASAP' : visit.label,
                    contact,
                    notes,
                    items: items.map(item => ({ title: item.title, detail: item.detail, priceLabel: item.priceLabel ?? '' })),
                    entry,
                    page: router.asPath,
                    photo: photo?.dataUrl ?? null,
                    website
                })
            })

            const data = await response.json().catch(() => null) as { ok?: boolean } | null

            if (!response.ok || !data?.ok) throw new Error('Request failed')

            setResult({ name: contact.name.trim().split(' ')[0], phone: contact.phone.trim(), when })
            setStatus('done')

            if (items.length) clear()
        }
        catch {
            setStatus('error')
        }
    }

    const goNext = () => {
        const found = validate(step)

        setErrors(found)

        if (Object.values(found).some(Boolean)) return

        if (step === 'contact') {
            void submit()

            return
        }

        const nextStep = steps[safeIndex + 1]

        if (nextStep === 'timing' && !visit.slot && urgentToday) {
            setVisit({ day: '', slot: ASAP_SLOT, label: "as soon as possible" })
        }

        setDirection(1)
        setStepIndex(safeIndex + 1)
    }

    const goBack = () => {
        setErrors({})
        setStatus(current => current === 'error' ? 'idle' : current)
        setDirection(-1)
        setStepIndex(Math.max(0, safeIndex - 1))
    }

    const reset = () => {
        setService(null)
        setStepIndex(0)
        setZip('')
        setAnswers({})
        setUrgency('')
        setVisit(EMPTY_VISIT)
        setContact({ name: '', phone: '', email: '', company: '' })
        setNotes('')
        setPhoto(null)
        setErrors({})
        setStatus('idle')
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status !== 'submitting') goNext()
    }

    const titles: Record<StepId, string> = {
        service: "What can we help with?",
        zip: "Where is the garage?",
        details: "Tell us a little about the door",
        urgency: config?.urgency.label ?? "How soon do you need us?",
        timing: "Pick a time for the visit",
        contact: "Where should your price go?"
    }

    const helpers: Partial<Record<StepId, string>> = {
        zip: "You'll see right away whether you're inside our service area.",
        details: "Only what this job calls for. A best guess is fine.",
        timing: urgentToday ? "Requests for today are routed to the on-call technician first." : undefined,
        contact: "Someone from our team replies in about 30 minutes during business hours."
    }

    if (status === 'done') {
        return (
            <div
                ref={rootRef}
                className={cn('rounded-[32px] border border-ink/[0.08] bg-paper p-7 text-center shadow-[0_40px_100px_-50px_rgba(21,23,26,0.45)] sm:p-12', className)}
            >
                <span className='mx-auto flex size-16 items-center justify-center rounded-full bg-moss text-white animate-in zoom-in duration-500'>
                    <Check className='size-7' aria-hidden='true' />
                </span>

                <h3
                    ref={headingRef}
                    tabIndex={-1}
                    className='mt-7 font-heading text-[2.2rem] font-semibold tracking-[-0.04em] outline-none'
                >
                    {result.name ? `Thank you, ${result.name}.` : "Thank you."}
                </h3>

                <p className='mx-auto mt-4 max-w-md text-lead text-slate'>
                    Expect a text at <span className='font-mono text-ink'>{result.phone}</span> in about 30 minutes during business hours, confirming your visit {result.when}.
                </p>

                <p className='mt-4 text-[15px] text-slate'>
                    Can't wait that long? Call{' '}
                    <a href={siteConfig.phoneHref} className='font-mono text-ink underline-offset-4 hover:underline'>
                        {siteConfig.phone}
                    </a>
                    .
                </p>

                <button
                    type='button'
                    onClick={reset}
                    className='mt-9 inline-flex h-11 items-center rounded-full border border-ink/15 px-5 text-sm font-medium transition-colors hover:border-ink/40'
                >
                    Start a new request
                </button>
            </div>
        )
    }

    const ServiceIcon = service ? serviceIcons[service] : null

    return (
        <div
            ref={rootRef}
            className={cn('relative rounded-[32px] border border-ink/[0.08] bg-paper p-5 shadow-[0_40px_100px_-50px_rgba(21,23,26,0.45)] sm:p-8 lg:p-10', className)}
        >
            <form onSubmit={handleSubmit} noValidate>
                <div aria-hidden='true' className='absolute -left-[9999px] h-0 w-0 overflow-hidden'>
                    <label>
                        Website
                        <input tabIndex={-1} autoComplete='off' value={website} onChange={event => setWebsite(event.target.value)} />
                    </label>
                </div>

                <div className='flex items-center justify-between gap-4'>
                    <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-slate'>
                        Step {safeIndex + 1} of {steps.length}
                    </p>

                    {service && safeIndex > 0 && ServiceIcon && config && (
                        <button
                            type='button'
                            onClick={() => {
                                setDirection(-1)
                                setStepIndex(0)
                            }}
                            className='inline-flex max-w-[60%] items-center gap-2 truncate rounded-full bg-sand px-3 py-1.5 text-[13px] font-medium text-ink transition-colors hover:bg-line'
                        >
                            <ServiceIcon className='size-3.5 shrink-0' aria-hidden='true' />
                            <span className='truncate'>{config.label}</span>
                            <span className='text-slate'>Change</span>
                        </button>
                    )}
                </div>

                <div className='mt-4 h-1 overflow-hidden rounded-full bg-sand' aria-hidden='true'>
                    <div
                        className='h-full rounded-full bg-ember transition-[width] duration-700 ease-expo'
                        style={{ width: `${((safeIndex + 1) / steps.length) * 100}%` }}
                    />
                </div>

                {items.length > 0 && (
                    <div className='mt-6 rounded-2xl border border-ink/[0.08] bg-card p-4'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>
                            Including {items.length} {items.length === 1 ? "item" : "items"} from your list
                        </p>

                        <ul className='mt-3 space-y-2'>
                            {items.map(item => (
                                <li key={item.key} className='flex items-center justify-between gap-3 text-[14px]'>
                                    <span className='min-w-0 truncate'>
                                        <span className='font-medium text-ink'>{item.title}</span>
                                        <span className='text-slate'> · {item.detail}</span>
                                    </span>

                                    <button
                                        type='button'
                                        onClick={() => remove(item.key)}
                                        aria-label={`Remove ${item.title} from this request`}
                                        className='flex size-8 shrink-0 items-center justify-center rounded-full text-slate transition-colors hover:bg-sand hover:text-ink'
                                    >
                                        <Trash2 className='size-3.5' />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div
                    key={`${step}-${service}`}
                    className={cn(
                        'mt-8 animate-in fade-in duration-500',
                        direction === 1 ? 'slide-in-from-right-6' : 'slide-in-from-left-6'
                    )}
                >
                    <h3
                        ref={headingRef}
                        tabIndex={-1}
                        id='quote-step-title'
                        className='font-heading text-[1.75rem] font-semibold leading-[1.1] tracking-[-0.035em] text-ink outline-none sm:text-[2.1rem]'
                    >
                        {titles[step]}
                    </h3>

                    {helpers[step] && (
                        <p className='mt-2 text-[15px] text-slate'>{helpers[step]}</p>
                    )}

                    <div className='mt-7'>
                        {step === 'service' && (
                            <div role='radiogroup' aria-labelledby='quote-step-title' className='grid gap-2 sm:grid-cols-2'>
                                {formServices.map(option => {
                                    const Icon = serviceIcons[option.id]
                                    const selected = service === option.id

                                    return (
                                        <button
                                            key={option.id}
                                            type='button'
                                            role='radio'
                                            aria-checked={selected}
                                            onClick={() => chooseService(option.id)}
                                            className={cn(
                                                'group flex min-h-16 items-center gap-4 rounded-2xl border px-4 py-3 text-left transition-[background-color,border-color,color] duration-300',
                                                selected ? 'border-ink bg-ink text-cream' : 'border-ink/12 bg-card text-ink hover:border-ink/35',
                                                option.id === 'emergency' && !selected && 'border-ember/30 bg-ember-tint/40'
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'flex size-10 shrink-0 items-center justify-center rounded-xl transition-colors',
                                                    selected ? 'bg-ember text-white' : option.id === 'emergency' ? 'bg-ember text-white' : 'bg-sand text-ink'
                                                )}
                                            >
                                                <Icon className='size-[18px]' aria-hidden='true' />
                                            </span>

                                            <span className='text-[15.5px] font-medium'>{option.label}</span>
                                        </button>
                                    )
                                })}
                            </div>
                        )}

                        {step === 'zip' && (
                            <div>
                                <TextField
                                    label="ZIP code"
                                    value={zip}
                                    onChange={value => {
                                        setZip(value.replace(/\D/g, '').slice(0, 5))
                                        setErrors({})
                                    }}
                                    inputMode='numeric'
                                    autoComplete='postal-code'
                                    placeholder="40110"
                                    error={errors.zip}
                                    maxLength={5}
                                />

                                <div aria-live='polite'>
                                    {zipArea && (
                                        <p className='mt-4 flex items-start gap-3 rounded-2xl bg-moss/10 p-4 text-[15px] text-ink animate-in fade-in duration-300'>
                                            <CircleCheckBig className='mt-0.5 size-5 shrink-0 text-moss' aria-hidden='true' />
                                            <span>
                                                {zipArea.name} is in our area. On urgent calls, a technician typically arrives in {zipArea.arrival}.
                                            </span>
                                        </p>
                                    )}

                                    {/^\d{5}$/.test(zip) && !zipArea && (
                                        <p className='mt-4 flex items-start gap-3 rounded-2xl bg-sand p-4 text-[15px] text-ink animate-in fade-in duration-300'>
                                            <Navigation className='mt-0.5 size-5 shrink-0 text-amber' aria-hidden='true' />
                                            <span>
                                                That ZIP sits outside our 8 towns. Send the request anyway and we'll tell you quickly whether we can help.
                                            </span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {step === 'details' && (
                            <div className='space-y-8'>
                                {visibleQuestions.map(question => (
                                    <div key={question.id}>
                                        <p id={`question-${question.id}`} className='flex items-baseline justify-between gap-3 text-[15.5px] font-semibold text-ink'>
                                            {question.label}
                                            {question.optional && <span className='text-[12.5px] font-normal text-slate'>Optional</span>}
                                        </p>

                                        {question.help && <p className='mt-1 text-[13.5px] text-slate'>{question.help}</p>}

                                        <div className='mt-3'>
                                            {question.type === 'text' ? (
                                                <textarea
                                                    aria-labelledby={`question-${question.id}`}
                                                    value={typeof answers[question.id] === 'string' ? answers[question.id] as string : ''}
                                                    onChange={event => setAnswer(question.id, event.target.value)}
                                                    rows={4}
                                                    maxLength={800}
                                                    placeholder="Grinds as it opens, then stops about halfway."
                                                    className='w-full rounded-2xl border border-ink/15 bg-card px-4 py-3 text-[16px] text-ink outline-none transition-colors placeholder:text-slate/55 focus:border-ember'
                                                />
                                            ) : (
                                                <OptionGrid
                                                    options={question.options ?? []}
                                                    value={answers[question.id]}
                                                    multiple={question.type === 'multi'}
                                                    onChange={value => setAnswer(question.id, value, question.type === 'multi')}
                                                    labelledBy={`question-${question.id}`}
                                                    invalid={Boolean(errors[question.id])}
                                                />
                                            )}
                                        </div>

                                        {errors[question.id] && <p className='mt-2 text-[13px] text-ember'>{errors[question.id]}</p>}
                                    </div>
                                ))}

                                <PhotoInput label={config?.photoLabel ?? "Add a photo (optional)"} value={photo} onChange={setPhoto} />

                                <div>
                                    <label htmlFor='quote-notes' className='flex items-baseline justify-between text-[15.5px] font-semibold text-ink'>
                                        Anything else the technician should know?
                                        <span className='text-[12.5px] font-normal text-slate'>Optional</span>
                                    </label>

                                    <textarea
                                        id='quote-notes'
                                        value={notes}
                                        onChange={event => setNotes(event.target.value)}
                                        rows={3}
                                        maxLength={800}
                                        className='mt-3 w-full rounded-2xl border border-ink/15 bg-card px-4 py-3 text-[16px] text-ink outline-none transition-colors focus:border-ember'
                                    />
                                </div>
                            </div>
                        )}

                        {step === 'urgency' && config && (
                            <div>
                                <OptionGrid
                                    options={config.urgency.options}
                                    value={urgency}
                                    columns={3}
                                    onChange={value => {
                                        setUrgency(value)
                                        setVisit(EMPTY_VISIT)
                                        setErrors({})
                                    }}
                                    labelledBy='quote-step-title'
                                    invalid={Boolean(errors.urgency)}
                                />

                                {errors.urgency && <p className='mt-2 text-[13px] text-ember'>{errors.urgency}</p>}
                            </div>
                        )}

                        {step === 'timing' && (
                            <div>
                                <TimingPicker
                                    value={visit}
                                    showAsap={urgentToday}
                                    asapPreselected={urgentToday}
                                    onChange={value => {
                                        setVisit(value)
                                        setErrors({})
                                    }}
                                />

                                {errors.timing && <p className='mt-3 text-[13px] text-ember'>{errors.timing}</p>}
                            </div>
                        )}

                        {step === 'contact' && (
                            <div className='grid gap-5'>
                                <div className='grid gap-5 sm:grid-cols-2'>
                                    <TextField
                                        label="Your name"
                                        value={contact.name}
                                        onChange={value => setContact(current => ({ ...current, name: value }))}
                                        autoComplete='name'
                                        error={errors.name}
                                    />

                                    <TextField
                                        label="Mobile number"
                                        type='tel'
                                        value={contact.phone}
                                        onChange={value => setContact(current => ({ ...current, phone: value }))}
                                        autoComplete='tel'
                                        inputMode='tel'
                                        placeholder="(555) 555-0123"
                                        error={errors.phone}
                                        maxLength={24}
                                    />
                                </div>

                                {config?.askCompany && (
                                    <TextField
                                        label="Company"
                                        value={contact.company}
                                        onChange={value => setContact(current => ({ ...current, company: value }))}
                                        autoComplete='organization'
                                        optional
                                    />
                                )}

                                <TextField
                                    label="Email"
                                    type='email'
                                    value={contact.email}
                                    onChange={value => setContact(current => ({ ...current, email: value }))}
                                    autoComplete='email'
                                    inputMode='email'
                                    error={errors.email}
                                    optional
                                />

                                <p className='flex items-start gap-2 text-[13px] leading-relaxed text-slate'>
                                    <MapPin className='mt-0.5 size-3.5 shrink-0' aria-hidden='true' />
                                    <span>
                                        {zip}{zipArea ? `, ${zipArea.name}` : ''}. These details are used only to price and book this visit.{' '}
                                        <TransitionLink href='/privacy' className='underline underline-offset-2 hover:text-ink'>
                                            Privacy policy
                                        </TransitionLink>
                                    </span>
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {status === 'error' && (
                    <p role='alert' className='mt-6 flex items-start gap-3 rounded-2xl bg-ember-tint p-4 text-[14.5px] text-ink'>
                        <TriangleAlert className='mt-0.5 size-4 shrink-0 text-ember' aria-hidden='true' />
                        <span>
                            Your request didn't go through. Please try again, or call{' '}
                            <a href={siteConfig.phoneHref} className='font-mono underline underline-offset-2'>{siteConfig.phone}</a>
                            {' '}and we'll take the details by phone.
                        </span>
                    </p>
                )}

                {errors.service && <p className='mt-4 text-[13px] text-ember'>{errors.service}</p>}

                <div className='mt-9 flex items-center justify-between gap-3 border-t border-ink/[0.08] pt-6'>
                    {safeIndex > 0 ? (
                        <button
                            type='button'
                            onClick={goBack}
                            className='inline-flex h-12 items-center gap-2 rounded-full px-4 text-[15px] font-medium text-slate transition-colors hover:text-ink'
                        >
                            <ArrowLeft className='size-4' aria-hidden='true' />
                            Back
                        </button>
                    ) : (
                        <span className='text-[13px] text-slate'>About 60 seconds, start to finish</span>
                    )}

                    <Button
                        type='submit'
                        disabled={status === 'submitting'}
                        icon={status === 'submitting' ? LoaderCircle : 'arrow'}
                        className={cn(status === 'submitting' && '[&_svg]:animate-spin')}
                    >
                        {step === 'contact' ? (status === 'submitting' ? "Sending" : "Get my price") : "Next step"}
                    </Button>
                </div>
            </form>
        </div>
    )
}
